'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import type { CSSProperties, ElementType, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import Tooltip from '../tooltip';
export type TruncateProps = {
  children: ReactNode;
  as?: 'span' | 'p' | 'div';
  className?: string;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  lines?: number;
  direction?: 'horizontal' | 'vertical';
  onTruncate?: (isTruncated: boolean) => void;
};
const Truncate = t0 => {
  const $ = _c(23);
  const {
    children,
    as: t1,
    className,
    tooltipSide: t2,
    lines,
    direction: t3,
    onTruncate
  } = t0;
  const Component = t1 === undefined ? "span" : t1;
  const tooltipSide = t2 === undefined ? "bottom" : t2;
  const direction = t3 === undefined ? "horizontal" : t3;
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  let t4;
  if ($[0] !== direction || $[1] !== onTruncate) {
    t4 = () => {
      const checkTruncation = () => {
        const element = textRef.current;
        if (!element) {
          return;
        }
        const isTruncatedAlongTheDirection = direction === "vertical" ? element.scrollHeight > element.clientHeight : element.scrollWidth > element.offsetWidth;
        setIsTruncated(isTruncatedAlongTheDirection);
        onTruncate?.(isTruncatedAlongTheDirection);
      };
      checkTruncation();
      const resizeObserver = new ResizeObserver(checkTruncation);
      if (textRef.current) {
        resizeObserver.observe(textRef.current);
      }
      return () => {
        resizeObserver.disconnect();
      };
    };
    $[0] = direction;
    $[1] = onTruncate;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== children || $[4] !== direction || $[5] !== lines || $[6] !== onTruncate) {
    t5 = [children, lines, direction, onTruncate];
    $[3] = children;
    $[4] = direction;
    $[5] = lines;
    $[6] = onTruncate;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t4, t5);
  const Tag = Component as ElementType;
  let t6;
  if ($[8] !== direction || $[9] !== lines) {
    t6 = direction === "vertical" ? {
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: lines,
      overflow: "hidden"
    } : undefined;
    $[8] = direction;
    $[9] = lines;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const lineClampStyle = t6;
  const t7 = direction === "horizontal" && "truncate";
  let t8;
  if ($[11] !== className || $[12] !== t7) {
    t8 = cx(t7, className);
    $[11] = className;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== Tag || $[15] !== children || $[16] !== lineClampStyle || $[17] !== t8) {
    t9 = <Tag ref={textRef} className={t8} style={lineClampStyle}>{children}</Tag>;
    $[14] = Tag;
    $[15] = children;
    $[16] = lineClampStyle;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const textElement = t9;
  if (isTruncated) {
    let t10;
    if ($[19] !== children || $[20] !== textElement || $[21] !== tooltipSide) {
      t10 = <Tooltip content={children} side={tooltipSide} fullWidth={true}>{textElement}</Tooltip>;
      $[19] = children;
      $[20] = textElement;
      $[21] = tooltipSide;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    return t10;
  }
  return textElement;
};
export default Truncate;